import React, { useEffect, useId, useRef, useState } from "react";
import { AnimatePresence, motion, useMotionTemplate, useMotionValue, useReducedMotion, useScroll, useSpring } from "framer-motion";
import { courseThemes, fallbackTheme, landmarkSlots } from "../../data/courseThemes";
import { ThemePattern } from "./ThemePattern";
import { LandmarkFigure } from "./LandmarkFigure";
import { getLanguage } from "../../utils/language";
interface ThemedBackdropProps {
  target: string;
  /** "full" = landmarks + pattern; "quiet" = pattern only (used during lessons). */
  mode?: "full" | "quiet";
  /** Leave room for the desktop app rail. */
  withRail?: boolean;
}

/**
 * A soft, country-themed backdrop for /app/learn/*: faint line drawings of famous places
 * and a tile pattern. A torch-like spotlight follows the cursor and lights them up in the
 * country's colours; clicking a landmark says its name.
 */
export function ThemedBackdrop({
  target,
  mode = "full",
  withRail = true
}: ThemedBackdropProps) {
  const theme = courseThemes[target] ?? fallbackTheme;
  const lang = getLanguage(target);
  const reduce = useReducedMotion() ?? false;
  const patternId = useId().replace(/:/g, "");
  const ref = useRef<HTMLDivElement>(null);
  const [discovered, setDiscovered] = useState(false);
  const showLandmarks = mode === "full" && theme.landmarks.length > 0;
  const nx = useMotionValue(0);
  const ny = useMotionValue(0);
  const px = useMotionValue(-1000);
  const py = useMotionValue(-1000);
  const pointerX = useSpring(nx, {
    stiffness: 50,
    damping: 18
  });
  const pointerY = useSpring(ny, {
    stiffness: 50,
    damping: 18
  });
  const {
    scrollY
  } = useScroll();
  const mask = useMotionTemplate`radial-gradient(240px circle at ${px}px ${py}px, #000 0%, transparent 70%)`;
  useEffect(() => {
    if (reduce) return;
    const onMove = (e: PointerEvent) => {
      if (e.pointerType !== "mouse") return;
      const r = ref.current?.getBoundingClientRect();
      if (!r) return;
      px.set(e.clientX - r.left);
      py.set(e.clientY - r.top);
      nx.set((e.clientX - r.left) / r.width * 2 - 1);
      ny.set((e.clientY - r.top) / r.height * 2 - 1);
    };
    const onLeave = () => {
      px.set(-1000);
      py.set(-1000);
    };
    window.addEventListener("pointermove", onMove, {
      passive: true
    });
    document.addEventListener("pointerleave", onLeave);
    return () => {
      window.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerleave", onLeave);
    };
  }, [reduce, nx, ny, px, py]);
  const figures = (lit: boolean) => theme.landmarks.map((l, i) => <LandmarkFigure key={`${target}-${l.art}`} landmark={l} slot={landmarkSlots[i % landmarkSlots.length]} speechLang={lang.speech} accent={theme.accent} accent2={theme.accent2} pointerX={pointerX} pointerY={pointerY} scrollY={scrollY} reduce={reduce} lit={lit} onDiscover={lit ? undefined : () => setDiscovered(true)} data-id="element-789" data-mp="vfzx136c0uja" data-mpi={i} />);
  return <div ref={ref} aria-hidden={showLandmarks ? undefined : true} className={`pointer-events-none fixed inset-0 z-0 overflow-hidden bg-paper ${withRail ? "lg:left-64" : ""}`} data-id="element-790" data-mp="3kxzwkzeyugzd">
      {/* Faint colour wash in the country's tone */}
      <div className="absolute inset-0" style={{
      backgroundColor: `rgb(${theme.accent} / 0.04)`
    }} data-id="element-791" data-mp="3fzezh8v0gdf5" />

      <ThemePattern id={`${patternId}-base`} pattern={theme.pattern} className="absolute inset-0 text-ink opacity-[0.045]" data-id="element-792" data-mp="7k51abu8rscn" />

      {showLandmarks && <div className="absolute inset-0 hidden xl:block" data-id="element-793" data-mp="chey4cjyn6ai">{figures(false)}</div>}

      {/* Flashlight: tinted copies of the tiles and landmarks, revealed only around the cursor */}
      {!reduce && <motion.div className="absolute inset-0" style={{
      WebkitMaskImage: mask,
      maskImage: mask
    }} data-id="element-794" data-mp="2nek1l6paqpn">
          <ThemePattern id={`${patternId}-glow`} pattern={theme.pattern} className="absolute inset-0" style={{
        color: `rgb(${theme.accent} / 0.32)`
      }} data-id="element-795" data-mp="2iekcxxst90pz" />
          {showLandmarks && <div className="absolute inset-0 hidden xl:block" style={{
        color: `rgb(${theme.accent} / 0.75)`
      }} data-id="element-796" data-mp="2yrp3w6fiexpe">
              {figures(true)}
            </div>}
        </motion.div>}

      {showLandmarks && <AnimatePresence data-id="element-797" data-mp="2ov417rqypyoq">
          {!discovered && <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: 1,
        transition: {
          delay: 1.2,
          duration: 0.3
        }
      }} exit={{
        opacity: 0,
        transition: {
          duration: 0.2
        }
      }} className="absolute bottom-6 right-8 hidden text-xs font-medium text-ink-2/70 xl:block" data-id="element-798" data-mp="3mh9o0sneg109">
              Tip: shine your cursor on the landmarks — click one to hear its name
            </motion.p>}
        </AnimatePresence>}
    </div>;
}